import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSaleSelection } from '../hooks/useSaleSelection';
import SaleContextBanner from '../components/admin/SaleContextBanner';

interface InvoiceItem {
  lotNumber: string;
  sku: string;
  title: string;
  quantity: number;
  highBid: number;
}

interface Invoice {
  invoiceNumber: string;
  auctionId: string;
  bidderNumber: string;
  bidder: {
    firstName: string;
    lastName: string;
    phone: string;
  };
  items: InvoiceItem[];
  total: number;
  status: string;
  pickStatus?: 'pending' | 'picked' | 'ready_for_pickup';
  storageLocation?: string;
  pickedAt?: string;
}

const PickerInterface: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [storageNotes, setStorageNotes] = useState<{[key: string]: string}>({});
  const [lastKeyTime, setLastKeyTime] = useState(0);
  const [sortBy, setSortBy] = useState<'lotNumber' | 'sku'>('sku');
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [completingInvoice, setCompletingInvoice] = useState<string | null>(null);
  const [storageLocationInput, setStorageLocationInput] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [scanMode, setScanMode] = useState<'search' | 'storage'>('search');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    picked: 0,
    ready: 0,
  });

  /*
   * The sale being picked. This replaces a local loadAuctions() that ordered on
   * `createdAt` desc and took the first row — which is how the Picker came to
   * default to a LiveAuctioneers import from months earlier. createdAt is when
   * the RECORD was typed in, not when the sale ran.
   *
   * It also fixes something quieter: orderBy('createdAt') OMITS documents that
   * lack the field, so any sale without one could not be chosen here at all.
   * The hook queries unordered and ranks in memory.
   *
   * The sentinel `selectedAuction === 'latest'` is gone too. On first render it
   * made the invoice filter match EVERY sale, so the stats tiles briefly showed
   * warehouse-wide totals before snapping to one sale.
   */
  const selection = useSaleSelection({ role: 'fulfillment', storageKey: 'picker' });
  const { saleId } = selection;

  const loadStats = useCallback(async () => {
    if (!saleId) return;
    try {
      const snapshot = await getDocs(collection(db, 'invoices'));
      const invoices: Invoice[] = [];

      snapshot.forEach(docSnap => {
        const invoice = docSnap.data() as Invoice;
        if (invoice.auctionId === saleId) invoices.push(invoice);
      });

      setStats({
        total: invoices.length,
        pending: invoices.filter(inv => !inv.pickStatus || inv.pickStatus === 'pending').length,
        picked: invoices.filter(inv => inv.pickStatus === 'picked').length,
        ready: invoices.filter(inv => inv.pickStatus === 'ready_for_pickup').length,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, [saleId]);

  // Reruns whenever the operator switches sale.
  useEffect(() => { loadStats(); }, [loadStats]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'invoices'));
      const results: Invoice[] = [];
      
      snapshot.forEach(docSnap => {
        const invoice = docSnap.data() as Invoice;
        const search = searchTerm.toLowerCase();
        
        // Safely check each field for undefined/null
        const bidderNumber = String(invoice.bidderNumber || '').toLowerCase();
        const firstName = String(invoice.bidder?.firstName || '').toLowerCase();
        const lastName = String(invoice.bidder?.lastName || '').toLowerCase();
        const invoiceNumber = String(invoice.invoiceNumber || '').toLowerCase();
        
        // Search by bidder number, name, lot number, or SKU
        const matchesBidder = bidderNumber.includes(search);
        const matchesName = `${firstName} ${lastName}`.includes(search);
        const matchesInvoice = invoiceNumber.includes(search);
        const matchesItems = (invoice.items || []).some(item => 
          String(item.lotNumber || '').toLowerCase().includes(search) || 
          String(item.sku || '').toLowerCase().includes(search) ||
          String(item.title || '').toLowerCase().includes(search)
        );
        
        if (matchesBidder || matchesName || matchesInvoice || matchesItems) {
          results.push(invoice);
        }
      });

      setSearchResults(results);
      
      if (results.length === 0) {
        toast.error('No invoices found');
      } else if (results.length === 1) {
        toast.success(`Found invoice for ${results[0].bidder.lastName}`);
      } else {
        toast.success(`Found ${results.length} invoice(s)`);
      }
    } catch (error) {
      console.error('Error searching:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchTerm(value);
    
    // Barcode scanner detection: if typing fast (< 100ms between chars), assume barcode scanner
    const now = Date.now();
    const timeSinceLastKey = now - lastKeyTime;
    setLastKeyTime(now);
    
    // If we got multiple characters quickly and it looks like a SKU/barcode
    if (timeSinceLastKey < 100 && value.length >= 5) {
      // Auto-search after a brief delay to ensure full barcode is captured
      setTimeout(() => {
        if (value.length >= 5) {
          handleSearch();
        }
      }, 150);
    }
  };

  const handleMarkPicked = async (invoice: Invoice) => {
    try {
      const storageLocation = storageNotes[invoice.invoiceNumber] || '';
      
      await updateDoc(doc(db, 'invoices', invoice.invoiceNumber), {
        pickStatus: 'picked',
        storageLocation: storageLocation || undefined,
        pickedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      toast.success('Marked as picked!');
      setStorageNotes(prev => ({ ...prev, [invoice.invoiceNumber]: '' }));
      loadStats();
      
      // Update in results
      setSearchResults(prev => 
        prev.map(inv => 
          inv.invoiceNumber === invoice.invoiceNumber 
            ? { ...inv, pickStatus: 'picked' as const, storageLocation }
            : inv
        )
      );
    } catch (error) {
      console.error('Error updating:', error);
      toast.error('Update failed');
    }
  };

  const handleMarkReady = async (invoice: Invoice) => {
    try {
      await updateDoc(doc(db, 'invoices', invoice.invoiceNumber), {
        pickStatus: 'ready_for_pickup',
        updatedAt: new Date().toISOString()
      });

      toast.success('Marked as ready for pickup!');
      loadStats();
      
      setSearchResults(prev => 
        prev.map(inv => 
          inv.invoiceNumber === invoice.invoiceNumber 
            ? { ...inv, pickStatus: 'ready_for_pickup' as const }
            : inv
        )
      );
    } catch (error) {
      console.error('Error updating:', error);
      toast.error('Update failed');
    }
  };

  const toggleItemChecked = (sku: string, invoiceNumber: string, allItemsInInvoice: string[]) => {
    setCheckedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sku)) {
        newSet.delete(sku);
      } else {
        newSet.add(sku);
        
        // Check if this completes the invoice
        const allChecked = allItemsInInvoice.every(itemSku => 
          newSet.has(itemSku) || itemSku === sku
        );
        
        if (allChecked) {
          // All items for this invoice are now checked - prompt for storage
          setTimeout(() => {
            setCompletingInvoice(invoiceNumber);
          }, 300); // Small delay for UX
        }
      }
      return newSet;
    });
  };

  const handleCompleteOrder = async () => {
    if (!completingInvoice || !storageLocationInput.trim()) {
      toast.error('Please enter a storage location');
      return;
    }

    try {
      const invoice = searchResults.find(inv => inv.invoiceNumber === completingInvoice);
      if (!invoice) return;

      const updatedItems = invoice.items.map(item => ({
        ...item,
        picked: true
      }));

      await updateDoc(doc(db, 'invoices', completingInvoice), {
        items: updatedItems,
        pickStatus: 'picked',
        storageLocation: storageLocationInput,
        pickedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      toast.success(`Order completed! Stored at: ${storageLocationInput}`);
      
      // Clear the checked items for this invoice
      setCheckedItems(prev => {
        const newSet = new Set(prev);
        invoice.items.forEach(item => newSet.delete(item.sku));
        return newSet;
      });
      
      // Remove from search results
      setSearchResults(prev => prev.filter(inv => inv.invoiceNumber !== completingInvoice));
      
      setCompletingInvoice(null);
      setStorageLocationInput('');
      loadStats();
    } catch (error) {
      console.error('Error completing order:', error);
      toast.error('Failed to complete order');
    }
  };

  const handleCancelComplete = () => {
    setCompletingInvoice(null);
    setStorageLocationInput('');
  };

  const handleBarcodeDetected = (barcode: string) => {
    if (scanMode === 'storage') {
      // Scanning for storage location during order completion
      setStorageLocationInput(barcode);
      setShowScanner(false);
      toast.success(`Storage location set: ${barcode}`);
    } else {
      // Normal search mode
      setSearchTerm(barcode);
      setShowScanner(false);
      // Auto-search after setting the term
      setTimeout(() => {
        handleSearch();
      }, 100);
    }
  };

  const openScanner = (mode: 'search' | 'storage' = 'search') => {
    setScanMode(mode);
    setShowScanner(true);
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <h1 className="text-3xl font-bold">📦 Warehouse Picker</h1>

      {/* Sale in view — above the numbers, because every number below is scoped to it. */}
      <SaleContextBanner selection={selection} label="Picking" />

      {/* Stats Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Total Orders</div>
          <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
        </div>
        
        <Link 
          to={`/auctions/${saleId}/invoices?pickFilter=to_be_picked`}
          className="bg-orange-50 hover:bg-orange-100 rounded-lg shadow p-4 transition cursor-pointer border-2 border-orange-200 hover:border-orange-400"
        >
          <div className="text-sm text-orange-600 font-semibold">Pending - Click to Pick</div>
          <div className="text-3xl font-bold text-orange-600">{stats.pending}</div>
          <div className="text-xs text-orange-500 mt-1">→ View all unpicked orders</div>
        </Link>
        
        <Link
          to="/pickup-orders"
          className="bg-blue-50 hover:bg-blue-100 rounded-lg shadow p-4 transition cursor-pointer border-2 border-blue-200 hover:border-blue-400"
        >
          <div className="text-sm text-blue-600 font-semibold">Picked - Click for Pickup</div>
          <div className="text-3xl font-bold text-blue-600">{stats.picked}</div>
          <div className="text-xs text-blue-500 mt-1">→ Manage customer pickups</div>
        </Link>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-600">Ready</div>
          <div className="text-3xl font-bold text-green-600">{stats.ready}</div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="mb-3">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
            <span className="font-semibold">📱 Barcode Scanner Ready:</span>
            <span>Scan with handheld scanner, or use camera button</span>
          </div>
        </div>
        <div className="flex gap-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearchInputChange(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Scan barcode or type SKU, Lot #, Bidder #, Name..."
            className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            autoFocus
          />
          <button
            onClick={() => openScanner('search')}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-lg"
            title="Scan with camera"
          >
            📷 Scan
          </button>
          <button
            onClick={handleSearch}
            disabled={loading}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50 text-lg"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {/* Results */}
      {searchResults.length > 0 && (
        <div className="space-y-4">
          {/* Sort Controls */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="font-semibold">Sort by:</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSortBy('lotNumber')}
                    className={`px-4 py-2 rounded-lg font-semibold transition ${
                      sortBy === 'lotNumber'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Lot Number
                  </button>
                  <button
                    onClick={() => setSortBy('sku')}
                    className={`px-4 py-2 rounded-lg font-semibold transition ${
                      sortBy === 'sku'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    SKU Number
                  </button>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                {checkedItems.size} / {searchResults.reduce((sum, inv) => sum + inv.items.length, 0)} items checked
              </div>
            </div>
          </div>

          {/* Thumbnail Grid */}
          <PickerGrid 
            searchResults={searchResults}
            sortBy={sortBy}
            checkedItems={checkedItems}
            toggleItemChecked={toggleItemChecked}
          />
        </div>
      )}

      {/* Order Completion Modal */}
      {completingInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4">✓ All Items Picked!</h2>
            
            {(() => {
              const invoice = searchResults.find(inv => inv.invoiceNumber === completingInvoice);
              return invoice ? (
                <>
                  <div className="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="font-semibold text-green-900">
                      {invoice.bidder.lastName}, {invoice.bidder.firstName}
                    </div>
                    <div className="text-sm text-green-700">
                      Bidder #{invoice.bidderNumber}
                    </div>
                    <div className="text-sm text-green-700">
                      {invoice.items.length} items - ${invoice.total.toFixed(2)}
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block font-semibold mb-2">
                      Where did you store this order?
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={storageLocationInput}
                        onChange={(e) => setStorageLocationInput(e.target.value)}
                        placeholder="e.g., Shelf A-12, Cart 3, Bay 5..."
                        className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                        autoFocus
                        onKeyPress={(e) => e.key === 'Enter' && handleCompleteOrder()}
                      />
                      <button
                        onClick={() => openScanner('storage')}
                        className="px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                        title="Scan barcode for storage location"
                      >
                        📷
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Type or scan storage location barcode</p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleCancelComplete}
                      className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCompleteOrder}
                      disabled={!storageLocationInput.trim()}
                      className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Complete Order
                    </button>
                  </div>
                </>
              ) : null;
            })()}
          </div>
        </div>
      )}

      {/* Camera Barcode Scanner */}
      {showScanner && (
        <BarcodeScanner
          onDetected={handleBarcodeDetected}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
};

// Barcode Scanner Component
const BarcodeScanner: React.FC<{
  onDetected: (barcode: string) => void;
  onClose: () => void;
}> = ({ onDetected, onClose }) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const scannerRef = React.useRef<any>(null);

  useEffect(() => {
    startScanning();
    return () => {
      stopScanning();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startScanning = async () => {
    try {
      // First, request camera permission explicitly
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      
      // Store stream for cleanup
      setStream(stream);
      
      // Stop the permission test stream
      stream.getTracks().forEach(track => track.stop());
      
      // Now start actual scanning with ZXing
      const { BrowserMultiFormatReader } = await import('@zxing/browser');
      
      const codeReader = new BrowserMultiFormatReader();
      scannerRef.current = codeReader;
      
      // Get video devices
      const videoInputDevices = await BrowserMultiFormatReader.listVideoInputDevices();
      
      if (videoInputDevices.length === 0) {
        setError('No camera found on this device');
        return;
      }

      // Try to use back camera on mobile, otherwise use first available
      const backCamera = videoInputDevices.find(device => 
        device.label.toLowerCase().includes('back') || 
        device.label.toLowerCase().includes('rear') ||
        device.label.toLowerCase().includes('environment')
      );
      const selectedDevice = backCamera || videoInputDevices[0];

      setIsScanning(true);

      // Start continuous scanning
      codeReader.decodeFromVideoDevice(
        selectedDevice.deviceId,
        videoRef.current!,
        (result, error) => {
          if (result) {
            // Barcode detected!
            const barcode = result.getText();
            console.log('Barcode detected:', barcode);
            toast.success(`Barcode scanned: ${barcode}`);
            
            // Stop scanning and return result
            stopScanning();
            onDetected(barcode);
          }
          
          // Errors are normal during scanning, ignore them
          if (error && !(error.name === 'NotFoundException')) {
            console.error('Scan error:', error);
          }
        }
      );

    } catch (err: any) {
      console.error('Scanner error:', err);
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera access denied. Please allow camera access in your browser settings and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else if (err.name === 'NotReadableError') {
        setError('Camera is already in use by another app.');
      } else {
        setError('Unable to access camera. Please check your browser permissions.');
      }
      setIsScanning(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    startScanning();
  };

  const stopScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.reset();
      scannerRef.current = null;
    }
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setIsScanning(false);
  };

  const handleManualEntry = () => {
    stopScanning();
    onClose(); // Close scanner modal
  };

  const handleClose = () => {
    stopScanning();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-gray-900 p-4 flex justify-between items-center">
        <h2 className="text-white text-xl font-bold">
          📷 {isScanning ? 'Scanning...' : 'Camera Ready'}
        </h2>
        <button
          onClick={handleClose}
          className="text-white text-3xl hover:text-gray-300 px-3"
        >
          ×
        </button>
      </div>

      {/* Camera View */}
      <div className="flex-1 flex items-center justify-center p-4">
        {error ? (
          <div className="text-center max-w-md">
            <div className="text-6xl mb-4">📷</div>
            <p className="text-white text-lg mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={handleRetry}
                className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
              >
                🔄 Try Again
              </button>
              <button
                onClick={handleManualEntry}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
              >
                ✍️ Enter Manually
              </button>
            </div>
            <div className="mt-6 p-4 bg-gray-800 rounded-lg text-left">
              <p className="text-white text-sm font-semibold mb-2">💡 Troubleshooting:</p>
              <ul className="text-gray-300 text-xs space-y-1">
                <li>• Make sure you clicked "Allow" when prompted</li>
                <li>• Check browser settings for camera permissions</li>
                <li>• Try closing other apps using the camera</li>
                <li>• Refresh the page and try again</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="relative max-w-2xl w-full">
            <video
              ref={videoRef}
              className="w-full rounded-lg"
              style={{ maxHeight: '60vh' }}
            />
            
            {/* Scanning Guide */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="relative">
                <div className="border-4 border-green-500 w-64 h-32 rounded-lg shadow-lg animate-pulse"></div>
                {isScanning && (
                  <div className="absolute -bottom-8 left-0 right-0 text-center">
                    <span className="text-green-500 text-sm font-semibold">
                      Scanning...
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-gray-900 p-4">
        <p className="text-white text-center mb-2">
          {isScanning 
            ? 'Position barcode within the green box - detection is automatic'
            : 'Starting camera...'}
        </p>
        <p className="text-gray-400 text-sm text-center mb-4">
          Works with most barcode formats (UPC, EAN, Code 128, etc.)
        </p>
        <button
          onClick={handleManualEntry}
          className="w-full px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 font-semibold"
        >
          Cancel - Type Barcode Manually
        </button>
      </div>
    </div>
  );
};

// Separate component for the grid to handle async image loading
const PickerGrid: React.FC<{
  searchResults: Invoice[];
  sortBy: 'lotNumber' | 'sku';
  checkedItems: Set<string>;
  toggleItemChecked: (sku: string, invoiceNumber: string, allItemsInInvoice: string[]) => void;
}> = ({ searchResults, sortBy, checkedItems, toggleItemChecked }) => {
  const [items, setItems] = useState<Array<{
    item: InvoiceItem;
    invoice: Invoice;
    thumbnail: string | null;
    allImages: string[];
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [galleryImages, setGalleryImages] = useState<string[] | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, sortBy]);

  const loadItems = async () => {
    setLoading(true);
    
    const allItems: Array<{
      item: InvoiceItem;
      invoice: Invoice;
      thumbnail: string | null;
      allImages: string[];
    }> = [];

    // Get auction data for images
    const auctionIds = Array.from(new Set(searchResults.map(inv => inv.auctionId)));
    const auctionImages: {[auctionId: string]: any} = {};

    for (const auctionId of auctionIds) {
      try {
        const auctionDoc = await getDoc(doc(db, 'auctions', auctionId));
        if (auctionDoc.exists()) {
          auctionImages[auctionId] = auctionDoc.data();
        }
      } catch (error) {
        console.error('Error loading auction:', error);
      }
    }

    // Build items array with images
    searchResults.forEach(invoice => {
      const auction = auctionImages[invoice.auctionId];
      
      invoice.items.forEach(item => {
        let thumbnail: string | null = null;
        let allImages: string[] = [];
        
        if (auction && auction.lots) {
          const lot = auction.lots.find((l: any) => 
            l.sku === item.sku || l.lotNumber === item.lotNumber
          );
          
          if (lot && lot.images && lot.images.length > 0) {
            allImages = lot.images;
            
            // Find _01.jpg image for thumbnail
            const image01 = lot.images.find((img: string) => 
              img.includes('_01.jpg') || img.includes('_01.')
            );
            if (image01) {
              thumbnail = image01;
            } else if (lot.images.length > 1) {
              thumbnail = lot.images[1];
            } else {
              thumbnail = lot.images[0];
            }
          }
        }
        
        allItems.push({ item, invoice, thumbnail, allImages });
      });
    });

    // Sort items
    const sorted = allItems.sort((a, b) => {
      if (sortBy === 'lotNumber') {
        const aNum = parseInt(a.item.lotNumber) || 0;
        const bNum = parseInt(b.item.lotNumber) || 0;
        return aNum - bNum;
      } else {
        const aNum = parseInt(a.item.sku) || 0;
        const bNum = parseInt(b.item.sku) || 0;
        return aNum - bNum;
      }
    });

    setItems(sorted);
    setLoading(false);
  };

  const openGallery = (images: string[], e: React.MouseEvent) => {
    e.stopPropagation();
    if (images.length > 0) {
      setGalleryImages(images);
      setCurrentImageIndex(0);
    }
  };

  const closeGallery = () => {
    setGalleryImages(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (galleryImages) {
      setCurrentImageIndex((prev) => 
        prev < galleryImages.length - 1 ? prev + 1 : 0
      );
    }
  };

  const prevImage = () => {
    if (galleryImages) {
      setCurrentImageIndex((prev) => 
        prev > 0 ? prev - 1 : galleryImages.length - 1
      );
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <p className="text-gray-600">Loading items...</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {items.map(({ item, invoice, thumbnail, allImages }) => {
          const isChecked = checkedItems.has(item.sku);
          const allItemSkus = invoice.items.map(i => i.sku);
          
          return (
            <div
              key={`${invoice.invoiceNumber}-${item.sku}`}
              className="relative bg-white rounded-lg shadow-lg overflow-hidden"
            >
              {/* Checked Overlay */}
              {isChecked && (
                <div 
                  onClick={() => toggleItemChecked(item.sku, invoice.invoiceNumber, allItemSkus)}
                  className="absolute inset-0 bg-green-500 bg-opacity-20 flex items-center justify-center z-10 cursor-pointer"
                >
                  <div className="bg-green-500 text-white rounded-full p-3">
                    <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              )}

              {/* Image - Clickable to check/uncheck */}
              <div 
                onClick={() => !isChecked && toggleItemChecked(item.sku, invoice.invoiceNumber, allItemSkus)}
                className="cursor-pointer"
              >
                {thumbnail ? (
                  <img
                    src={thumbnail}
                    alt={item.title}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-400 text-sm">No Image</span>
                  </div>
                )}
              </div>

              {/* Item Info */}
              <div className="p-3">
                <div className="font-bold text-sm mb-1">Lot {item.lotNumber}</div>
                <div className="text-xs text-gray-600 mb-1 line-clamp-2">{item.title}</div>
                <div className="text-xs text-gray-500 mb-2">SKU: {item.sku}</div>
                
                {/* Bidder Info - Prominent */}
                <div className="bg-blue-50 border border-blue-200 rounded p-2 mb-2">
                  <div className="font-bold text-blue-900 text-sm">
                    Bidder #{invoice.bidderNumber}
                  </div>
                  <div className="text-xs text-blue-700">
                    {invoice.bidder.lastName}, {invoice.bidder.firstName}
                  </div>
                </div>

                {/* Action Buttons - Stacked for mobile */}
                <div className="space-y-1">
                  {allImages.length > 1 && (
                    <button
                      onClick={(e) => openGallery(allImages, e)}
                      className="w-full px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                    >
                      📸 View All {allImages.length} Images
                    </button>
                  )}
                  
                  <Link
                    to={`/invoices/${invoice.invoiceNumber}`}
                    onClick={(e) => e.stopPropagation()}
                    className="block w-full px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 text-center"
                  >
                    📄 Invoice
                  </Link>
                  
                  <Link
                    to={`/picker/order/${invoice.invoiceNumber}`}
                    onClick={(e) => e.stopPropagation()}
                    className="block w-full px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 text-center"
                  >
                    📦 Pick Order
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Image Gallery Modal */}
      {galleryImages && galleryImages.length > 0 && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeGallery}
        >
          <button
            onClick={closeGallery}
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 z-50"
          >
            ×
          </button>

          {galleryImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-4 text-white text-6xl hover:text-gray-300 z-50"
              >
                ‹
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 text-white text-6xl hover:text-gray-300 z-50"
              >
                ›
              </button>
            </>
          )}

          <div
            className="max-w-5xl max-h-[90vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryImages[currentImageIndex]}
              alt={`Image ${currentImageIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded"
            />
            {galleryImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-75 text-white px-4 py-2 rounded">
                {currentImageIndex + 1} / {galleryImages.length}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PickerInterface;